import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronDownIcon, MenuIcon, PhoneIcon, XIcon } from 'lucide-react';
import { company, media, navLinks, routes } from '../data/site';
import { allServiceGroups } from '../data/services';

// Customer priority: residential landscapes and land development lead;
// ongoing commercial care follows, with the overview as a separate final link.
const serviceLinks = [...allServiceGroups.filter(g => Boolean(g.pageHref)).map(g => ({
  label: g.title.replace(/\b[a-z]/g, letter => letter.toUpperCase()),
  href: g.pageHref as string
})), {
  label: 'Services Overview',
  href: routes.services
}];
export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const menuToggleRef = useRef<HTMLButtonElement>(null);
  const servicesToggleRef = useRef<HTMLButtonElement>(null);
  const servicesWrapRef = useRef<HTMLLIElement>(null);
  const reduce = useReducedMotion();
  const path = typeof window !== 'undefined' ? window.location.pathname.replace(/\/+$/, '') || '/' : '/';

  // Mobile panel: Escape closes and restores focus; widening closes.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        menuToggleRef.current?.focus();
      }
    };
    const onResize = () => {
      if (window.innerWidth >= 1280) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  // Desktop services dropdown: Escape restores toggle focus; click/touch outside closes.
  useEffect(() => {
    if (!servicesOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setServicesOpen(false);
        servicesToggleRef.current?.focus();
      }
    };
    const onPointer = (e: PointerEvent) => {
      if (!servicesWrapRef.current?.contains(e.target as Node)) setServicesOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const onResize = () => {
      if (window.innerWidth < 1280) setServicesOpen(false);
    };
    document.addEventListener('pointerdown', onPointer);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
      window.removeEventListener('resize', onResize);
    };
  }, [servicesOpen]);
  const linkClass = 'focus-ring whitespace-nowrap text-[14px] tracking-[0.02em] text-ivory/85 transition-colors duration-150 hover:text-ivory';
  return <header className="sticky top-0 z-50 bg-olive text-ivory" data-id="element-379" data-mp="27htaa4mw01o4">
      <div className="site-container flex h-[72px] items-center justify-between gap-6 lg:h-[92px]" data-id="element-380" data-mp="22fkgj626wvky">
        <a href="/#top" className="focus-ring block shrink-0" aria-label={`${company.name} — back to top`} data-id="element-381" data-mp="29z3imun4lfje">
          <img src={media.logo} alt={company.name} className="h-auto w-[172px] sm:w-[200px] lg:w-[240px]" data-id="element-382" data-mp="2f00z92shm1is" />
        </a>

        <nav aria-label="Primary" className="hidden xl:block" data-id="element-383" data-mp="2119nvo50etyc">
          <ul className="flex items-center gap-9" data-id="element-384" data-mp="25237i5bf5mlf">
            {navLinks.map(link => link.href === routes.services ? <li key={link.href} ref={servicesWrapRef} className="relative flex items-center gap-1" onBlur={e => {
            const next = e.relatedTarget as Node | null;
            if (next && !e.currentTarget.contains(next)) setServicesOpen(false);
          }} data-id="element-385" data-mp="2sspgbyt8zawh">
                  <a href={link.href} aria-current={path === link.href ? 'page' : undefined} className={linkClass} data-id="element-386" data-mp="3bo76lxgsn80r">
                    {link.label}
                  </a>
                  <button ref={servicesToggleRef} type="button" aria-expanded={servicesOpen} aria-controls="services-dropdown" aria-label={servicesOpen ? 'Hide service pages' : 'Show service pages'} onClick={() => setServicesOpen(v => !v)} className="focus-ring -mr-2 inline-flex h-11 w-11 items-center justify-center text-ivory/80 transition-colors duration-150 hover:text-ivory" data-id="element-387" data-mp="1ssx0zc5t5ys9">
                    <ChevronDownIcon className={`h-4 w-4 transition-transform duration-200 ease-out ${servicesOpen ? 'rotate-180' : ''}`} strokeWidth={1.5} aria-hidden="true" data-id="element-388" data-mp="26ld7imxx4exx" />
                  </button>
                  <AnimatePresence data-id="element-389" data-mp="2ua614cr2iapd">
                    {servicesOpen && <motion.div id="services-dropdown" className="absolute left-0 top-[calc(100%+28px)] w-[340px] border border-ivory/10 bg-olive py-3 shadow-[0_16px_32px_rgba(0,0,0,0.25)]" initial={reduce ? false : {
                opacity: 0,
                y: -4
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={reduce ? {
                opacity: 0
              } : {
                opacity: 0,
                y: -4
              }} transition={{
                duration: reduce ? 0 : 0.16,
                ease: [0.23, 1, 0.32, 1]
              }} data-id="element-390" data-mp="2phcvznrot0cr">
                        <ul data-id="element-391" data-mp="zwxjogdydboj">
                          {serviceLinks.map((s, i) => <li key={s.href} className={s.href === routes.services ? 'mt-1 border-t border-ivory/10 pt-1' : ''} data-id="element-392" data-mp="14ofojfj3jwes" data-mpi={i}>
                              <a href={s.href} aria-current={path === s.href ? 'page' : undefined} onClick={() => setServicesOpen(false)} className={`focus-ring block px-5 py-2.5 text-[14.5px] leading-snug transition-colors duration-150 hover:bg-ivory/5 hover:text-ivory ${i < 2 ? 'font-medium text-ivory' : 'text-ivory/85'}`} data-id="element-393" data-mp="18opsynr8qerj">
                                {s.label}
                              </a>
                            </li>)}
                        </ul>
                      </motion.div>}
                  </AnimatePresence>
                </li> : <li key={link.href} data-id="element-394" data-mp="3tic3l497ht0j">
                  <a href={link.href} aria-current={path === link.href ? 'page' : undefined} className={linkClass} data-id="element-395" data-mp="bams3n8357jh">
                    {link.label}
                  </a>
                </li>)}
          </ul>
        </nav>

        <div className="hidden items-center gap-6 xl:flex" data-id="element-396" data-mp="2d3u3wmohp70m">
          <a href={company.phoneHref} aria-label={`Call ${company.phoneDisplay}`} className="focus-ring inline-flex items-center gap-2 whitespace-nowrap text-[14px] text-ivory/85 transition-colors duration-150 hover:text-ivory" data-id="element-397" data-mp="283oiho8tcgqz">
            <PhoneIcon className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" data-id="element-398" data-mp="dvcc162shcbf" />
            <span className="hidden xl:inline" data-id="element-399" data-mp="itm8dk092iym">{company.phoneDisplay}</span>
          </a>
          <a href="/contact" className="focus-ring whitespace-nowrap border border-ivory/60 px-5 py-2.5 text-[13.5px] font-medium tracking-[0.02em] transition-colors duration-150 hover:border-ivory hover:bg-ivory hover:text-olive" data-id="element-400" data-mp="4u4eiq83kfie">
            Start your project
          </a>
        </div>

        <button ref={menuToggleRef} type="button" className="focus-ring -mr-2 inline-flex h-11 w-11 shrink-0 items-center justify-center xl:hidden" aria-expanded={open} aria-controls="mobile-menu" aria-label={open ? 'Close menu' : 'Open menu'} onClick={() => setOpen(v => !v)} data-id="element-401" data-mp="3vzw2pk2bdi2k">
          {open ? <XIcon className="h-6 w-6" strokeWidth={1.5} data-id="element-402" data-mp="i1ifef4t4ycm" /> : <MenuIcon className="h-6 w-6" strokeWidth={1.5} data-id="element-403" data-mp="d1nq2g0okgto" />}
        </button>
      </div>

      <AnimatePresence data-id="element-404" data-mp="894bbfq9bfna">
        {open && <motion.div id="mobile-menu" className="absolute inset-x-0 top-full max-h-[calc(100vh-var(--header-h))] overflow-y-auto overscroll-contain border-t border-ivory/10 bg-olive shadow-[0_16px_32px_rgba(0,0,0,0.25)] xl:hidden" initial={reduce ? false : {
        opacity: 0,
        y: -6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        y: -6
      }} transition={{
        duration: reduce ? 0 : 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-405" data-mp="87t93kcgmfya">
            <nav aria-label="Mobile" className="site-container py-4" data-id="element-406" data-mp="1jp1arxvq1hyr">
              <ul data-id="element-407" data-mp="1jo9ppgzr32mq">
                {navLinks.map(link => link.href === routes.services ? <li key={link.href} className="border-b border-ivory/10 pb-4 pt-4" data-id="element-408" data-mp="1eo5bjxmbejzo">
                      <a id="mobile-services-label" href={routes.services} aria-current={path === routes.services ? 'page' : undefined} onClick={() => setOpen(false)} className="focus-ring block font-display text-[26px] leading-none text-ivory" data-id="element-409" data-mp="1m7eizlb9g8m3">
                        {link.label}
                      </a>
                      <ul aria-labelledby="mobile-services-label" className="ml-1 mt-3 border-l border-ivory/20 pl-4" data-id="element-410" data-mp="188wrdci8ibyw">
                        {serviceLinks.map((s, i) => <li key={s.href} className={s.href === routes.services ? 'mt-2 border-t border-ivory/10 pt-2' : ''} data-id="element-411" data-mp="1i7ma17hyg0g7" data-mpi={i}>
                            <a href={s.href} aria-current={path === s.href ? 'page' : undefined} onClick={() => setOpen(false)} className={`focus-ring flex min-h-[44px] items-center py-1.5 text-[16px] leading-snug ${i < 2 ? 'font-medium text-ivory' : 'text-ivory/85'}`} data-id="element-412" data-mp="2omwn0ba1rfgc">
                              {s.label}
                            </a>
                          </li>)}
                      </ul>
                    </li> : <li key={link.href} className="border-b border-ivory/10" data-id="element-413" data-mp="24y2tgdlwvbur">
                      <a href={link.href} aria-current={path === link.href ? 'page' : undefined} onClick={() => setOpen(false)} className="focus-ring block py-4 font-display text-[26px] leading-none text-ivory" data-id="element-414" data-mp="29y7b46yk2cmk">
                        {link.label}
                      </a>
                    </li>)}
              </ul>
              <div className="flex flex-col gap-3 pb-3 pt-6 sm:flex-row" data-id="element-415" data-mp="1eqwx3fvzrhnd">
                <a href={company.phoneHref} className="focus-ring inline-flex items-center justify-center gap-2 border border-ivory/40 px-5 py-3.5 text-[14px]" data-id="element-416" data-mp="19q99l46z1ixw">
                  <PhoneIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-417" data-mp="zzo3ywsvre5h" />
                  {company.phoneDisplay}
                </a>
                <a href="/contact" onClick={() => setOpen(false)} className="focus-ring inline-flex items-center justify-center bg-ivory px-5 py-3.5 text-[14px] font-medium text-olive" data-id="element-418" data-mp="qyq6kxcy64vv">
                  Start your project
                </a>
              </div>
            </nav>
          </motion.div>}
      </AnimatePresence>
    </header>;
}